import React, { useCallback, useRef, useState } from "react";
import NewCustomAsyncSelectDropdown from "../../../components/common/NewCustomAsyncSelectDropdown.jsx";

/**
 * Searchable dropdown backed by *real* server-side pagination — nothing is
 * fetched until the menu is opened, one page loads at a time, scrolling to
 * the bottom loads the next page, and typing re-queries the server (not a
 * client-side filter over an already-downloaded list).
 *
 * This replaces the old pattern used across the module's modals, where each
 * form eagerly called a `fetchAllX()` helper that looped through every page
 * of a list (trainers/courses/classrooms/branches/batches) sequentially,
 * front-loading dozens of requests before the dropdown could even be opened.
 *
 * `fetchPage(page, search)` must resolve to `{ data, pagination }` — every
 * `getX(limit, page, search, …)` service function in this app already
 * returns that shape for a single page; wrap it in a closure that pins the
 * limit, e.g. `(page, search) => getTrainers(20, page, search)`.
 *
 * `initialOption` ({ value, label }) is for the edit case: the record being
 * edited already carries its own name (e.g. `booking.trainerName`), so its
 * label can be shown immediately without waiting on — or depending on —
 * that specific record showing up in whatever page happens to load first.
 */
const AsyncPagedSelect = ({
  value,
  onChange,
  fetchPage,
  toOption,
  initialOption = null,
  placeholder = "Select…",
  isDisabled,
  // The id of the page's label for this field (aria-labelledby).
  labelId,
}) => {
  const [options, setOptions] = useState(initialOption ? [initialOption] : []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (page, search, replace) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const { data, pagination } = await fetchPage(page, search);
        if (requestId !== requestIdRef.current) return; // superseded by a newer search/scroll
        const mapped = (data || []).map(toOption);
        setOptions((prev) => {
          const base = replace ? [] : prev;
          // Keep the seeded "currently selected" option visible even if this
          // particular page doesn't happen to include it.
          const seed =
            replace && initialOption && !mapped.some((o) => o.value === initialOption.value)
              ? [initialOption]
              : [];
          const seen = new Set();
          return [...seed, ...base, ...mapped].filter((o) => {
            if (seen.has(o.value)) return false;
            seen.add(o.value);
            return true;
          });
        });
        const totalPages = Number(pagination?.totalPages) || 1;
        setHasMore(Boolean(pagination?.hasNext) || page < totalPages);
        pageRef.current = page;
      } catch (error) {
        console.error("AsyncPagedSelect: failed to load a page of options", error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [fetchPage, toOption, initialOption]
  );

  const handleMenuOpen = () => {
    if (!loadedOnce) {
      setLoadedOnce(true);
      loadPage(1, "", true);
    }
  };

  const handleInputChange = (term) => {
    searchRef.current = term;
    loadPage(1, term, true);
  };

  const handleScrollToBottom = () => {
    if (!hasMore || loading) return;
    loadPage(pageRef.current + 1, searchRef.current, false);
  };

  const selected = options.find((o) => o.value === value) || initialOption || null;

  return (
    <NewCustomAsyncSelectDropdown
      labelId={labelId}
      options={options}
      value={selected ? [selected] : []}
      // The whole option rides along as a second argument, so a caller that
      // needs the record behind it (e.g. a picked batch's trainer and room)
      // has it without asking the server again. Callers reading only the id
      // are unaffected.
      onChange={(vals) => onChange(vals[0]?.value ?? "", vals[0] || null)}
      onMenuOpen={handleMenuOpen}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={handleScrollToBottom}
      isLoading={loading}
      isMulti={false}
      isDisabled={isDisabled}
      placeholder={placeholder}
      loadingMessage="Loading…"
      noOptionsMessage="No options available"
      noSearchResultsMessage="No matches"
    />
  );
};

export default AsyncPagedSelect;
